import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, type SelectProps } from 'antd';
import { useClientContext } from 'pos-client/contexts/ClientContext.tsx';
import { useCurrentUser } from 'pos-client/hooks/useCurrentUser.ts';
import { useLocations } from 'pos-client/hooks/useLocations.tsx';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';

type OmitKeys =
  | 'showSearch'
  | 'options'
  | 'loading'
  | 'fieldNames'
  | 'optionFilterProp';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Props extends Omit<SelectProps<any, LocationModel>, OmitKeys> {
  showAllActiveLocations?: boolean;
  showInactiveLocations?: boolean;
  selectedLocationId?: string | string[] | null;
  disabledLocationIds?: string[];
}

export const LocationSelect = memo(
  ({
    showAllActiveLocations,
    showInactiveLocations,
    selectedLocationId,
    disabledLocationIds,
    ...props
  }: Props) => {
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const { locations, isLoadingLocations } = useLocations(
      {
        assignedUserId: showAllActiveLocations ? undefined : currentUser?.id,
      },
      {
        enabled: !!currentUser?.id,
      }
    );

    const showLocations = useMemo(
      () =>
        locations
          .filter((l) => (showInactiveLocations ? true : l.isActive))
          .map((location) => ({
            ...location,
            disabled: (disabledLocationIds || []).includes(location.id),
          })),
      [locations, showInactiveLocations, disabledLocationIds]
    );

    const locationId = useMemo(() => {
      if (showLocations && showLocations.length === 0) {
        return null;
      }
      return showLocations && showLocations.length === 1
        ? showLocations[0].id
        : props.value || selectedLocationId;
    }, [showLocations, props.value, selectedLocationId]);

    useEffect(() => {
      if (showLocations && showLocations.length === 1) {
        const values: [string | string[], LocationModel] =
          props.mode === 'multiple'
            ? [[showLocations[0].id], showLocations[0]]
            : [showLocations[0].id, showLocations[0]];

        if (props.onSelect) props.onSelect(...values);
        if (props.onChange) props.onChange(...values);
      }
    }, [showLocations, props.mode]);

    const { handleChangeClient } = useClientContext();

    useEffect(() => {
      if (locationId) {
        const selectedLocation = showLocations.find(
          (location) => location.id === locationId
        );
        if (selectedLocation) {
          handleChangeClient(selectedLocation);
        }
      } else if (locationId === undefined) {
        handleChangeClient(null);
      }
    }, [locationId, showLocations, handleChangeClient]);

    return (
      <Select
        {...props}
        defaultValue={selectedLocationId}
        loading={isLoadingLocations}
        optionFilterProp="name"
        options={showLocations}
        showSearch
        value={isLoadingLocations ? null : locationId}
        disabled={
          (showLocations && showLocations.length === 1) || props.disabled
        }
        fieldNames={{
          value: 'id',
          label: 'name',
        }}
        placeholder={
          isLoadingLocations
            ? `${t('common.states.loading')}...`
            : (props.placeholder ??
              t('common.placeholders.selectServiceLocation'))
        }
      />
    );
  }
);
